import { BaseModel } from '../BaseModel'
import { Endpoint } from '../endpoint/Endpoint'
import adminApiClient from './../../api/admin-api-client'
/**
 * Class Subdivision
 *
 * @extends BaseModel
 */
export class Subdivision extends BaseModel {
  constructor(props) {
    super(props)
    this.subdivisionId = props.subdivisionId || 0
    this.subdivisionName = props.subdivisionName || ''
    this.externalCompanyNumber = props.externalCompanyNumber || null
    this.externalCompanyId = props.externalCompanyId || ''
    this.endpointId = props.endpointId || null
    this.endpointName = props.endpointName || null
    this.endpoint = props.endpoint ? new Endpoint(props.endpoint) : new Endpoint({})
    this.userCount = props.userCount || null
    // these are not set by api, done manually
    this.implicit = props.implicit || false
    this.companyId = props.companyId || 0
    this.allowPersonalMail = Boolean(props.allowPersonalMail) || false
    this.useSubdivisonMailAccount = Boolean(props.useSubdivisonMailAccount) || false
    this.locale = props.locale || ''
    this.fiscalId = props.fiscalId || ''
    this.entityContact = props.entityContact || null
    this.entityAddress = props.entityAddress || null
    this.phoneNumber = props.phoneNumber || null
    this.commercialPhone = props.commercialPhone || null
    this.email = props.email || null
    this.invoicingEmail = props.invoicingEmail || null
    this.trainingEmail = props.trainingEmail || null
    this.primaryAddress = props.primaryAddress || null
    this.secondaryAddress = props.secondaryAddress || null
    this.invoicingAddress = props.invoicingAddress || null

    /**
     * Save the initial state of the object for comparing changes
     */
    this._initialValues = { ...this, _initialValues: null }
  }

  get text() {
    return this.subdivisionName
  }

  get id() {
    return this.subdivisionId
  }
  /**
   *
   */
  async update() {
    if (!this.companyId) {
      throw new Error('CompanyId is not set on subdivision')
    }

    this.isLoading = true
    // Fetch current entity contacts to get the IDs
    const entityContactsResponse = await adminApiClient.getSubdivisionEntityContacts(this.companyId, this.subdivisionId)

    // Map the contact IDs from the response
    const contactIds = {}
    entityContactsResponse.data.forEach((contact) => {
      switch (contact.contactTypeId) {
        case 1:
          contactIds.phoneId = contact.contactId
          break
        case 2:
          contactIds.commercialPhoneId = contact.contactId
          break
        case 3:
          contactIds.emailId = contact.contactId
          break
        case 4:
          contactIds.invoicingEmailId = contact.contactId
          break
        case 5:
          contactIds.trainingEmailId = contact.contactId
          break
      }
    })

    // Compare initial and current entity contact values
    const changedContacts = []
    if (this.entityContact && this._initialValues.entityContact) {
      const contactFields = ['phone', 'commercialPhone', 'email', 'invoicingEmail', 'trainingEmail']
      contactFields.forEach((field, index) => {
        if (this.entityContact[field] !== this._initialValues.entityContact[field]) {
          changedContacts.push({
            contactTypeId: index + 1,
            value: this.entityContact[field],
            contactId: contactIds[`${field}Id`],
          })
        }
      })
    }

    // Update changed contacts first
    for (const contact of changedContacts) {
      if (!contact.value) {
        // Delete contact if value is empty and contactId exists
        if (contact.contactId) {
          await adminApiClient.deleteEntityContact(contact.contactId)
        }
      } else if (contact.contactId) {
        // Update existing contact
        await adminApiClient.updateEntityContact(contact.contactId, {
          entityTypeId: 2,
          contactTypeId: contact.contactTypeId,
          contact: contact.value,
          entityId: this.subdivisionId,
        })
      } else {
        // Create new contact
        await adminApiClient.createEntityContact({
          entityTypeId: 2,
          contactTypeId: contact.contactTypeId,
          contact: contact.value,
          entityId: this.subdivisionId,
        })
      }
    }

    // Handle entity addresses
    if (this.entityAddress && this._initialValues.entityAddress) {
      const addressTypeMap = {
        1: 'primaryAddress',
        2: 'secondaryAddress',
        3: 'invoicingAddress',
      }

      // Get current addresses to map IDs
      const entityAddressesResponse = await adminApiClient.getSubdivisionEntityAddresses(
        this.companyId,
        this.subdivisionId,
      )

      const existingAddresses = {}
      entityAddressesResponse.data.forEach((address) => {
        existingAddresses[addressTypeMap[address.addressTypeId]] = address
      })

      // Update or create addresses
      for (const addressTypeId of Object.keys(addressTypeMap)) {
        const newAddress = this.entityAddress[addressTypeMap[addressTypeId]]
        const existingAddress = existingAddresses[addressTypeMap[addressTypeId]]
        const initialAddress = this._initialValues.entityAddress[addressTypeMap[addressTypeId]]

        if (!newAddress && existingAddress) {
          // Delete address if it was removed
          await adminApiClient.deleteEntityAddress(existingAddress.addressId)
        } else if (newAddress && !existingAddress) {
          // Create new address
          await adminApiClient.createEntityAddress({
            entityTypeId: 2,
            entityId: this.subdivisionId,
            addressTypeId: addressTypeId,
            ...newAddress,
          })
        } else if (newAddress && existingAddress && JSON.stringify(newAddress) !== JSON.stringify(initialAddress)) {
          // Update existing address if changed
          await adminApiClient.updateEntityAddress(existingAddress.addressId, {
            entityTypeId: 2,
            entityId: this.subdivisionId,
            addressTypeId: addressTypeId,
            ...newAddress,
          })
        }
      }
    }

    const data = {
      companyId: this.companyId,
      data: {
        subdivisionId: this.subdivisionId,
        subdivisionName: this.subdivisionName,
        externalCompanyId: this.externalCompanyId,
        externalCompanyNumber: this.externalCompanyNumber,
        endpointId: this.endpointId,
        allowPersonalMail: this.allowPersonalMail ? 1 : 0,
        useSubdivisonMailAccount: this.useSubdivisonMailAccount ? 1 : 0,
        fiscalId: this.fiscalId,
        locale: this.locale,
        entityContact: null,
        entityAddress: null,
      },
    }

    return adminApiClient
      .updateSubdivision(this.companyId, this.subdivisionId, data)
      .then((response) => {
        if (response.status === 200) {
          this.isLoading = false
          return true
        }
        throw new Error('Failed to save!')
      })
      .catch((ex) => {
        this.isLoading = false
        throw ex
      })
  }

  transformForComparison(model) {
    model.externalCompanyId = model.externalCompanyId || ''
    model.externalCompanyNumber = model.externalCompanyNumber || null
    return model
  }
}
